import type { PurchaseOrder } from "@/redistribution/models/PurchaseOrder.js";
import type { RedistributionResult } from "@/redistribution/models/RedistributionResult.js";
import type {
  DashboardFooterSummary,
  DashboardInventoryWinRow,
  DashboardPageData,
  DashboardRedistributionActivityRow,
} from "./dashboard.types.ts";

interface TraceStats {
  totalEntries: number;
  warnings: number;
  errors: number;
  durationLabel: string;
}

interface TraceReasonCounts {
  critical: number;
  moderate: number;
  balanced: number;
  makeRoom: number;
}

interface ParsedTraceContract {
  stats: TraceStats;
  reasonCounts?: TraceReasonCounts;
  matchedContract: boolean;
}

interface RiskCounts {
  critical: number;
  moderate: number;
  healthy: number;
}

export function buildDashboardPageDataFromRun(
  basePageData: DashboardPageData,
  options: {
    traceLog?: string;
    redistributionResult?: RedistributionResult;
    originalPOs?: PurchaseOrder[];
  }
): DashboardPageData {
  const { traceLog, redistributionResult, originalPOs = [] } = options;
  if (!redistributionResult) {
    return basePageData;
  }

  const movements = redistributionResult.movements ?? [];
  const parsedTrace = parseTraceContract(traceLog);
  const trace = parsedTrace.stats;
  const reasonCounts = parsedTrace.reasonCounts ?? {
    critical: 0,
    moderate: 0,
    balanced: 0,
    makeRoom: 0,
  };
  const unitsReallocated = movements.reduce((sum, movement) => {
    return sum + movement.quantityMoved;
  }, 0);
  const locationsProtected = new Set(movements.map((m) => m.toWarehouse)).size;
  const confidenceScore = computeConfidenceScore(trace);
  const riskBefore = summarizeRiskCounts(originalPOs);
  const riskAfter = summarizeRiskCounts(
    redistributionResult.updatedPurchaseOrderList
  );
  const highRiskBefore = riskBefore.critical + riskBefore.moderate;
  const highRiskAfter = riskAfter.critical + riskAfter.moderate;
  const highRiskReductionPct =
    highRiskBefore > 0
      ? Math.round(((highRiskBefore - highRiskAfter) / highRiskBefore) * 1000) /
        10
      : 0;

  const summaryStats = basePageData.summaryStats.map((stat) => {
    switch (stat.id) {
      case "recommended-movements": {
        return {
          ...stat,
          value: String(movements.length),
          detail: parsedTrace.matchedContract
            ? `Successful moves: ${String(trace.totalEntries)}`
            : `Successful moves: ${String(trace.totalEntries)}`,
        };
      }
      case "critical-stockouts": {
        return {
          ...stat,
          value: String(reasonCounts.critical),
          detail: "Critical-risk movements logged",
        };
      }
      case "moderate-risks": {
        return {
          ...stat,
          value: String(reasonCounts.moderate),
          detail: "Moderate-risk movements logged",
        };
      }
      case "locations-protected": {
        return {
          ...stat,
          value: String(locationsProtected),
          detail: "Destination locations receiving inventory",
        };
      }
      case "units-reallocated": {
        return {
          ...stat,
          value: String(unitsReallocated),
          detail: "Total units moved this run",
        };
      }
      case "wos-variance": {
        return {
          ...stat,
          value: `${String(Math.max(0, 100 - trace.warnings * 4 - trace.errors * 12))}%`,
          detail: `Trace warnings/errors: ${String(trace.warnings)}/${String(trace.errors)}`,
        };
      }
      default: {
        return stat;
      }
    }
  });

  const activityRows: DashboardRedistributionActivityRow[] = movements.map(
    (movement) => ({
      sku: movement.skuNumber,
      fromPurchaseOrder: movement.fromPurchaseOrder,
      fromDistributionCenter: `${movement.fromDivision}-${String(movement.fromWarehouse)}`,
      toPurchaseOrder: movement.toPurchaseOrder,
      toDistributionCenter: `${movement.toDivision}-${String(movement.toWarehouse)}`,
      unitsMoved: movement.quantityMoved,
      reason: toReasonLabel(movement.reason),
      reasonTone: toReasonTone(movement.reason),
    })
  );

  const inventoryBalancingCount = parsedTrace.matchedContract
    ? reasonCounts.balanced + reasonCounts.makeRoom
    : reasonCounts.balanced;

  type MovementWithComparableWos = (typeof movements)[number] & {
    wosBeforeWeeks: number;
    wosAfterWeeks: number;
  };

  const topInventoryWins: DashboardInventoryWinRow[] = movements
    .filter(
      (movement): movement is MovementWithComparableWos =>
        movement.wosBeforeWeeks != null &&
        movement.wosAfterWeeks != null &&
        movement.wosAfterWeeks - movement.wosBeforeWeeks > 0
    )
    .toSorted(
      (a, b) =>
        b.wosAfterWeeks -
        b.wosBeforeWeeks -
        (a.wosAfterWeeks - a.wosBeforeWeeks)
    )
    .slice(0, 5)
    .map((m) => ({
      sku: m.skuNumber,
      before: `${m.wosBeforeWeeks.toFixed(1)}w`,
      beforeTone: toneFromWos(m.wosBeforeWeeks),
      after: `${m.wosAfterWeeks.toFixed(1)}w`,
      afterTone: toneFromWos(m.wosAfterWeeks),
      improvement: `+${(m.wosAfterWeeks - m.wosBeforeWeeks).toFixed(1)}w`,
      outcome:
        m.reason === "CriticalRisk"
          ? "Critical stockout prevented"
          : m.reason === "ModerateRisk"
            ? "Moderate risk resolved"
            : m.reason === "BalanceWos"
              ? "WOS balanced"
              : "Cube capacity freed",
    }));

  const footerSummary: DashboardFooterSummary = {
    ...basePageData.footerSummary,
    confidenceScore: `${String(confidenceScore)}%`,
    confidenceLabel: confidenceLabel(confidenceScore),
    runStatus:
      trace.errors > 0
        ? "Run completed with errors"
        : trace.warnings > 0
          ? "Run completed with warnings"
          : "Run completed successfully",
    runCompletedAt:
      redistributionResult.traceLogBlobId === undefined
        ? basePageData.footerSummary.runCompletedAt
        : `Latest run trace: ${redistributionResult.traceLogBlobId}`,
    runDuration: trace.durationLabel,
    traceLogLabel: `View Algorithm Trace Log (${String(trace.totalEntries)} moves)`,
  };

  return {
    ...basePageData,
    lastRunLabel:
      redistributionResult.traceLogBlobId === undefined
        ? basePageData.lastRunLabel
        : `Latest run loaded (${redistributionResult.traceLogBlobId})`,
    filterSummary:
      trace.totalEntries > 0
        ? parsedTrace.matchedContract
          ? `Trace contract metrics active • ${String(trace.totalEntries)} successful moves`
          : `Trace-powered run metrics active • ${String(trace.totalEntries)} successful moves`
        : basePageData.filterSummary,
    summaryStats,
    reasonBreakdown: [
      {
        label: "Critical Stockout Prevention",
        value: reasonCounts.critical,
        color: "#ef4444",
      },
      {
        label: "Moderate Stockout Prevention",
        value: reasonCounts.moderate,
        color: "#f59e0b",
      },
      {
        label: "Inventory Balancing",
        value: inventoryBalancingCount,
        color: "#3b82f6",
      },
      {
        label: "Make Room",
        value: reasonCounts.makeRoom,
        color: "#10b981",
      },
    ],
    inventoryHealthBefore: [
      {
        label: "Critical",
        value: riskBefore.critical,
        colorClass: "dashboard-pill--critical",
      },
      {
        label: "Moderate",
        value: riskBefore.moderate,
        colorClass: "dashboard-pill--moderate",
      },
      {
        label: "Healthy",
        value: riskBefore.healthy,
        colorClass: "dashboard-pill--healthy",
      },
    ],
    inventoryHealthAfter: [
      {
        label: "Critical",
        value: riskAfter.critical,
        colorClass: "dashboard-pill--critical",
      },
      {
        label: "Moderate",
        value: riskAfter.moderate,
        colorClass: "dashboard-pill--moderate",
      },
      {
        label: "Healthy",
        value: riskAfter.healthy,
        colorClass: "dashboard-pill--healthy",
      },
    ],
    inventoryHealthSummary:
      highRiskBefore > 0
        ? `High + moderate risk locations reduced from ${String(highRiskBefore)} to ${String(highRiskAfter)} (-${String(highRiskReductionPct)}%).`
        : basePageData.inventoryHealthSummary,
    totalLocations: redistributionResult.updatedPurchaseOrderList.length,
    businessImpact: [
      {
        label: "Purchase Orders Optimized",
        value: String(redistributionResult.updatedPurchaseOrderList.length),
      },
      {
        label: "SKUs Rebalanced",
        value: String(new Set(movements.map((m) => m.skuNumber)).size),
      },
      {
        label: "Trace Warnings",
        value: String(trace.warnings),
        accent: trace.warnings > 0 ? "neutral" : "positive",
      },
      {
        label: "Trace Errors",
        value: String(trace.errors),
        accent: trace.errors > 0 ? "neutral" : "positive",
      },
      {
        label: "Successful Moves",
        value: String(trace.totalEntries),
        accent: "neutral",
      },
    ],
    redistributionActivity:
      activityRows.length > 0
        ? activityRows
        : basePageData.redistributionActivity,
    activityPaginationLabel:
      activityRows.length > 0
        ? `${String(movements.length)} total movements`
        : basePageData.activityPaginationLabel,
    topInventoryWins:
      topInventoryWins.length > 0
        ? topInventoryWins
        : basePageData.topInventoryWins,
    footerSummary,
  };
}

export function parseTraceContract(traceLog?: string): ParsedTraceContract {
  if (!traceLog) {
    return {
      stats: {
        totalEntries: 0,
        warnings: 0,
        errors: 0,
        durationLabel: "Duration: Not available",
      },
      matchedContract: false,
    };
  }

  // ── Try structured JSON contract first ────────────────────────────────
  if (traceLog.trimStart().startsWith("{")) {
    try {
      const json = JSON.parse(traceLog) as Record<string, unknown>;
      if ("runId" in json && "durationMs" in json) {
        const durationMs = Number(json.durationMs ?? 0);
        const moves = Array.isArray(json.moves) ? json.moves.length : 0;
        const successfulMoves = Number(json.successfulMoves ?? moves);
        return {
          stats: {
            totalEntries: successfulMoves,
            warnings: Number(json.warnings ?? 0),
            errors: Number(json.errors ?? 0),
            durationLabel: `Duration: ${String(durationMs)}ms`,
          },
          reasonCounts: {
            critical: Number(json.criticalRiskCount ?? 0),
            moderate: Number(json.moderateRiskCount ?? 0),
            balanced: Number(json.balanceWosCount ?? 0),
            makeRoom: Number(json.makeRoomCount ?? 0),
          },
          matchedContract: true,
        };
      }
    } catch {
      // Not valid JSON — fall through to legacy text parsing
    }
  }

  // ── Legacy plain-text parsing (backward compatible) ───────────────────
  const lines = traceLog
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const summaryLine = lines.find((line) => /^TRACE_SUMMARY\b/i.test(line));
  const reasonLine = lines.find((line) =>
    /^MOVEMENT_REASON_COUNTS\b/i.test(line)
  );

  const totalEntriesFromSummary = extractNumber(
    summaryLine,
    /totalEntries=(\d+)/i
  );
  const warningsFromSummary = extractNumber(summaryLine, /warnings=(\d+)/i);
  const errorsFromSummary = extractNumber(summaryLine, /errors=(\d+)/i);
  const durationFromSummary = extractString(
    summaryLine,
    /duration=([0-9]{1,2}:[0-9]{2}:[0-9]{2}|\d+ms)/i
  );

  const totalEntriesFromLabel = extractNumberFromLines(
    lines,
    /^TotalEntries\s*:\s*(\d+)$/i
  );
  const warningsFromLabel = extractNumberFromLines(
    lines,
    /^Warnings\s*:\s*(\d+)$/i
  );
  const errorsFromLabel = extractNumberFromLines(
    lines,
    /^Errors\s*:\s*(\d+)$/i
  );
  const durationFromLabel = extractStringFromLines(
    lines,
    /^Duration\s*:\s*([0-9]{1,2}:[0-9]{2}:[0-9]{2}|\d+ms)$/i
  );

  const durationValue =
    durationFromSummary ??
    durationFromLabel ??
    extractDurationFromHeuristics(lines);

  const statsFromHeuristics = parseTraceStatsFromHeuristics(lines);

  const stats: TraceStats = {
    totalEntries:
      totalEntriesFromSummary ??
      totalEntriesFromLabel ??
      statsFromHeuristics.totalEntries,
    warnings:
      warningsFromSummary ?? warningsFromLabel ?? statsFromHeuristics.warnings,
    errors: errorsFromSummary ?? errorsFromLabel ?? statsFromHeuristics.errors,
    durationLabel: durationValue
      ? `Duration: ${durationValue}`
      : statsFromHeuristics.durationLabel,
  };

  const reasonCounts = parseTraceReasonCounts(lines, reasonLine);

  const parsed: ParsedTraceContract = {
    stats,
    matchedContract:
      summaryLine !== undefined ||
      reasonLine !== undefined ||
      totalEntriesFromLabel !== undefined ||
      warningsFromLabel !== undefined ||
      errorsFromLabel !== undefined ||
      durationFromLabel !== undefined,
  };

  if (reasonCounts !== undefined) {
    parsed.reasonCounts = reasonCounts;
  }

  return parsed;
}

function parseTraceStatsFromHeuristics(lines: string[]): TraceStats {
  if (lines.length === 0) {
    return {
      totalEntries: 0,
      warnings: 0,
      errors: 0,
      durationLabel: "Duration: Not available",
    };
  }

  const warnings = lines.filter((line) => /\bwarn(ing)?\b/i.test(line)).length;
  const errors = lines.filter((line) => /\berror\b/i.test(line)).length;

  const durationValue = extractDurationFromHeuristics(lines);

  return {
    totalEntries: lines.length,
    warnings,
    errors,
    durationLabel: durationValue
      ? `Duration: ${durationValue}`
      : "Duration: Not available",
  };
}

function parseTraceReasonCounts(
  lines: string[],
  reasonLine?: string
): TraceReasonCounts | undefined {
  const scopedLine =
    reasonLine ?? lines.find((line) => /^ReasonCounts\b/i.test(line));

  const criticalFromScoped = extractNumber(
    scopedLine,
    /(?:critical|CriticalRisk)=(\d+)/i
  );
  const moderateFromScoped = extractNumber(
    scopedLine,
    /(?:moderate|ModerateRisk)=(\d+)/i
  );
  const balancedFromScoped = extractNumber(
    scopedLine,
    /(?:balanced|BalanceWos)=(\d+)/i
  );
  const makeRoomFromScoped = extractNumber(
    scopedLine,
    /(?:makeRoom|MakeRoom)=(\d+)/i
  );

  const criticalFromLabel = extractNumberFromLines(
    lines,
    /^CriticalRisk\s*:\s*(\d+)$/i
  );
  const moderateFromLabel = extractNumberFromLines(
    lines,
    /^ModerateRisk\s*:\s*(\d+)$/i
  );
  const balanceFromLabel = extractNumberFromLines(
    lines,
    /^BalanceWos\s*:\s*(\d+)$/i
  );
  const makeRoomFromLabel = extractNumberFromLines(
    lines,
    /^MakeRoom\s*:\s*(\d+)$/i
  );

  const critical = criticalFromScoped ?? criticalFromLabel;
  const moderate = moderateFromScoped ?? moderateFromLabel;
  const balanced = balancedFromScoped ?? balanceFromLabel;
  const makeRoom = makeRoomFromScoped ?? makeRoomFromLabel;

  if (
    critical === undefined &&
    moderate === undefined &&
    balanced === undefined &&
    makeRoom === undefined
  ) {
    return undefined;
  }

  return {
    critical: critical ?? 0,
    moderate: moderate ?? 0,
    balanced: balanced ?? 0,
    makeRoom: makeRoom ?? 0,
  };
}

function extractDurationFromHeuristics(lines: string[]): string | undefined {
  const durationLine = lines.find((line) =>
    /\b(duration|elapsed)\b/i.test(line)
  );
  return extractString(durationLine, /(\d{1,2}:\d{2}:\d{2}|\d+ms)/i);
}

function extractNumber(
  source: string | undefined,
  pattern: RegExp
): number | undefined {
  if (!source) return undefined;
  const match = source.match(pattern);
  if (!match?.[1]) return undefined;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function extractString(
  source: string | undefined,
  pattern: RegExp
): string | undefined {
  if (!source) return undefined;
  const match = source.match(pattern);
  return match?.[1] ?? undefined;
}

function extractNumberFromLines(
  lines: string[],
  pattern: RegExp
): number | undefined {
  for (const line of lines) {
    const parsed = extractNumber(line, pattern);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return undefined;
}

function extractStringFromLines(
  lines: string[],
  pattern: RegExp
): string | undefined {
  for (const line of lines) {
    const parsed = extractString(line, pattern);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return undefined;
}

function summarizeRiskCounts(purchaseOrders: PurchaseOrder[]): RiskCounts {
  let critical = 0;
  let moderate = 0;
  let healthy = 0;

  for (const po of purchaseOrders) {
    let highestUrgency = 0;
    for (const sku of po.skus) {
      highestUrgency = Math.max(highestUrgency, sku.urgency);
    }

    if (highestUrgency >= 2) {
      critical += 1;
    } else if (highestUrgency >= 1) {
      moderate += 1;
    } else {
      healthy += 1;
    }
  }

  return { critical, moderate, healthy };
}

function computeConfidenceScore(trace: TraceStats): number {
  return Math.max(
    0,
    Math.min(99, Math.round(100 - trace.errors * 12 - trace.warnings * 3))
  );
}

function confidenceLabel(score: number): string {
  if (score >= 90) return "High confidence in recommendations";
  if (score >= 75) return "Moderate confidence — review warnings";
  return "Low confidence — inspect trace log details";
}

function toReasonLabel(reason: string): string {
  switch (reason) {
    case "CriticalRisk": {
      return "Critical Stockout Prevention";
    }
    case "ModerateRisk": {
      return "Moderate Stockout Prevention";
    }
    case "BalanceWos": {
      return "Inventory Balancing";
    }
    default: {
      return "Make Room";
    }
  }
}

function toReasonTone(reason: string): "critical" | "moderate" | "balanced" {
  if (reason === "CriticalRisk") return "critical";
  if (reason === "ModerateRisk") return "moderate";
  return "balanced";
}

function toneFromWos(wos: number): DashboardInventoryWinRow["beforeTone"] {
  if (wos <= 0) return "critical";
  if (wos < 1.5) return "moderate";
  if (wos > 8) return "overstocked";
  if (wos >= 4) return "balanced";
  return "healthy";
}

/**
 * Safely stringify an unknown trace log field, falling back to a
 * placeholder instead of risking `[object Object]` output.
 */
function strField(value: unknown, fallback = "?"): string {
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
    ? String(value)
    : fallback;
}

/**
 * Render a structured JSON trace log contract as human-readable text.
 * Returns the raw input unchanged if it's not valid JSON.
 */
export function renderTraceLogAsText(traceLog: string): string {
  if (!traceLog.trimStart().startsWith("{")) {
    return traceLog;
  }

  let json: Record<string, unknown>;
  try {
    json = JSON.parse(traceLog) as Record<string, unknown>;
  } catch {
    return traceLog;
  }

  if (!("runId" in json)) {
    return traceLog;
  }

  const sep = "═══════════════════════════════════════════════════════════════";
  const lines: string[] = [
    sep,
    "SKU REDISTRIBUTION TRACE LOG",
    "  This log explains every move the algorithm made and WHY.",
    "  WOS = Weeks of Stock on the PO line (higher = more stock relative to demand).",
    "  Score = total unfilled demand-days across all SKUs/POs. Lower is better.",
    `  RunId: ${String(json.runId)} | Version: ${strField(json.algorithmVersion)} | InputSig: ${strField(json.inputSignature)}`,
    `  ROS window: ${String(json.rateOfSaleDays)} days | PO cube limit: ${String(json.poCube)} | POs in run: ${String(json.poCount)} | Starting score: ${Number(json.scoreBefore ?? 0).toFixed(2)}`,
    `  Optimization mode: ${strField(json.optimizationMode)}`,
  ];

  const poOrder = Array.isArray(json.poOrder)
    ? (json.poOrder as string[]).join(" -> ")
    : "?";
  lines.push(
    `  PO order (later arrivals first, so stock moves toward sooner-arriving POs): ${poOrder}`
  );

  const frozen =
    Array.isArray(json.frozenPoNumbers) && json.frozenPoNumbers.length > 0
      ? (json.frozenPoNumbers as string[]).join(", ")
      : undefined;
  if (frozen) {
    lines.push(
      `  FROZEN POs (ship date < 28 days out — no moves to/from): ${frozen}`
    );
  }
  lines.push(sep);

  // ── Individual moves ──────────────────────────────────────────────────
  const moves = Array.isArray(json.moves)
    ? (json.moves as Record<string, unknown>[])
    : [];
  if (moves.length > 0) {
    for (const m of moves) {
      const scoreDelta = Number(m.scoreDelta ?? 0);
      const deltaStr =
        scoreDelta >= 0 ? `+${scoreDelta.toFixed(2)}` : scoreDelta.toFixed(2);
      lines.push(
        `  SKU ${String(m.sku)}: [${String(m.from)}] -> [${String(m.to)}]  ` +
          `Qty: ${String(m.quantity)}  Reason: ${String(m.reason)}  ` +
          `Δscore=${deltaStr}  ` +
          `SrcWOS=${Number(m.srcWosBefore ?? 0).toFixed(2)} -> ${Number(m.srcWosAfter ?? 0).toFixed(2)} wks  ` +
          `TgtWOS=${Number(m.tgtWosBefore ?? 0).toFixed(2)} -> ${Number(m.tgtWosAfter ?? 0).toFixed(2)} wks`
      );
    }
    lines.push(sep);
  }

  // ── Metrics ───────────────────────────────────────────────────────────
  lines.push(
    sep,
    `METRICS  RunId=${String(json.runId)}  AlgorithmVersion=${strField(json.algorithmVersion)}  Timestamp=${strField(json.timestamp)}`,
    `  Execution:      TotalRuntimeMs=${String(json.durationMs)}  TotalIterations=${String(json.iterations)}  TotalMoveAttempts=${String(json.moveAttempts)}`,
    `  Moves:          SuccessfulMoves=${String(json.successfulMoves)}  Rollbacks=${String(json.rollbacks)}`,
    `  Score:          Before=${Number(json.scoreBefore ?? 0).toFixed(4)}  After=${Number(json.scoreAfter ?? 0).toFixed(4)}`
  );

  const reduction = Number(json.shortageReduction ?? 0);
  const reductionPct = Number(json.shortageReductionPct ?? 0);
  lines.push(
    `  ShortageReduction=${reduction.toFixed(4)} demand-days  (${reductionPct.toFixed(2)}% of before-score)`,
    `  Movements:      UnitsMovedTotal=${String(json.unitsMoved)}  UniqueSKUsMoved=${String(json.uniqueSkusMoved)}`
  );

  const movesByPo = json.movesByPo as Record<string, number> | undefined;
  if (movesByPo && Object.keys(movesByPo).length > 0) {
    lines.push(
      `  MovesByPO:      ${Object.entries(movesByPo)
        .map(([k, v]) => `${k}:${v >= 0 ? "+" : ""}${String(v)}`)
        .join(", ")}`
    );
  }

  lines.push(
    `  Safety:         ScoringEquivalenceCount=${String(json.scoringEquivalences)}  FailedMoveDueToConstraints=${String(json.failedDueToConstraints)}  InfiniteLoopGuardHits=${String(json.infiniteLoopGuardHits)}  MaxAttemptsPerUnit=${String(json.maxAttemptsPerUnit)}`,
    `  InputSignature: ${strField(json.inputSignature)}`,
    "* Score = total unfilled demand-days across all active SKUs. Lower is better.",
    "* Positive ShortageReduction = the algorithm reduced overall unfilled demand.",
    sep
  );

  return lines.join("\n");
}
